import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Card from "components/card";
import { getErrorMessage } from "utils/cms";
import { deleteEnquiry, getEnquiry } from "api/enquiry";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Row = ({ label, children }) => (
  <div className="grid gap-1 border-b border-gray-100 py-3 last:border-0 sm:grid-cols-[180px_1fr] sm:gap-6 dark:border-white/10">
    <dt className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#6A4CF0]">
      {label}
    </dt>
    <dd className="whitespace-pre-wrap text-sm text-navy-700 dark:text-white">
      {children || "—"}
    </dd>
  </div>
);

export default function EnquiryView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await getEnquiry(id);
        if (!cancelled) setEnquiry(res.data?.data || null);
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const onDelete = async () => {
    const label = enquiry?.name || enquiry?.email || "this enquiry";
    if (!window.confirm(`Delete enquiry from ${label}? This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    setError("");
    try {
      await deleteEnquiry(id);
      navigate("/admin/enquiries");
    } catch (err) {
      setError(getErrorMessage(err));
      setDeleting(false);
    }
  };

  return (
    <div className="mt-3">
      <Link
        to="/admin/enquiries"
        className="mb-3 inline-flex text-sm font-medium text-[#6A4CF0] hover:underline"
      >
        ← Back to enquiries
      </Link>

      <Card extra="overflow-hidden rounded-2xl border border-gray-200 shadow-none dark:!border-white/10">
        <div className="flex items-start justify-between gap-4 border-b border-purple-100 bg-gradient-to-r from-[#F3F0FF] to-white px-4 py-3 dark:border-white/10 dark:from-navy-800 dark:to-navy-800">
          <div>
            <h3 className="text-base font-bold text-navy-700 dark:text-white">
              Enquiry
            </h3>
            <p className="text-xs text-gray-500">
              {enquiry ? formatDate(enquiry.createdAt) : "Contact form submission"}
            </p>
          </div>
          {enquiry ? (
            <button
              type="button"
              disabled={deleting}
              onClick={onDelete}
              className="inline-flex rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10"
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          ) : null}
        </div>

        <div className="px-4 py-2">
          {error ? (
            <p className="py-6 text-sm text-red-500">{error}</p>
          ) : loading ? (
            <p className="py-6 text-sm text-gray-500">Loading enquiry…</p>
          ) : !enquiry ? (
            <p className="py-6 text-sm text-gray-500">Enquiry not found.</p>
          ) : (
            <dl>
              <Row label="Date">{formatDate(enquiry.createdAt)}</Row>
              <Row label="Name">{enquiry.name}</Row>
              <Row label="Email">
                {enquiry.email ? (
                  <a
                    href={`mailto:${enquiry.email}`}
                    className="text-[#6A4CF0] hover:underline"
                  >
                    {enquiry.email}
                  </a>
                ) : (
                  "—"
                )}
              </Row>
              <Row label="Phone">
                {enquiry.phone ? (
                  <a
                    href={`tel:${String(enquiry.phone).replace(/\s/g, "")}`}
                    className="text-[#6A4CF0] hover:underline"
                  >
                    {enquiry.phone}
                  </a>
                ) : (
                  "—"
                )}
              </Row>
              <Row label="How they heard">{enquiry.hear}</Row>
              <Row label="Reason">{enquiry.reason}</Row>
              <Row label="Project">{enquiry.project}</Row>
              <Row label="Privacy accepted">
                {enquiry.consent ? "Yes" : "No"}
              </Row>
            </dl>
          )}
        </div>
      </Card>
    </div>
  );
}
